import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import CustomerViewBanner from '@/components/CustomerViewBanner';
import EmailVerificationBanner from '@/components/EmailVerificationBanner';
import AIChatWidget from '@/components/AIChatWidget';
import BackButton from '@/components/BackButton';
import { NotificationsBell } from '@/components/NotificationsBell';
import MobileHeader from '@/components/MobileHeader';
import RolePickerDialog from '@/components/dialogs/RolePickerDialog';
import useEdgeSwipe from '@/hooks/useEdgeSwipe';
import { usePresentMode } from '@/contexts/PresentModeContext';
import { UpgradePromptProvider } from '@/components/UpgradePromptDialog';
import { useAuth } from '@/contexts/AuthContext';
import CalendarView from '@/components/CalendarView';
import CalendarSyncView from '@/components/CalendarSyncView';
import PropertiesView from '@/components/PropertiesView';
import BookingsView from '@/components/BookingsView';
import CleaningView from '@/components/CleaningView';
import MaintenanceView from '@/components/MaintenanceView';
import RenovationsView from '@/components/RenovationsView';
import ExpensesView from '@/components/ExpensesView';
import WorkersView from '@/components/WorkersView';
import ReportsView from '@/components/ReportsView';
import InvoicesView from '@/components/InvoicesView';
import WorkOrdersView from '@/components/WorkOrdersView';
import UsersView from '@/components/UsersView';
import BillingView from '@/components/BillingView';
import WaitlistView from '@/components/WaitlistView';
import EmailHealthView from '@/components/EmailHealthView';
import FilesView from '@/components/FilesView';
import AccountView from '@/components/AccountView';
import TeamChatView from '@/components/TeamChatView';
import DashboardHome from '@/components/DashboardHome';
import AnalyticsView from '@/components/AnalyticsView';
import { getProperties } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { presentMode } = usePresentMode();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close the mobile drawer whenever the route changes so it doesn't linger
  // after a tap.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Edge-swipe gestures: right-swipe from left edge opens the drawer,
  // left-swipe closes it.
  useEdgeSwipe({
    isOpen: sidebarOpen,
    onOpen: () => setSidebarOpen(true),
    onClose: () => setSidebarOpen(false),
  });

  const fetchProperties = useCallback(async () => {
    try {
      const response = await getProperties();
      setProperties(response.data);
      // Only auto-select first property on initial load, not on refreshes
      // This allows "All Properties" selection to persist
      if (response.data.length > 0 && !initialLoadDone) {
        setSelectedProperty(response.data[0]);
        setInitialLoadDone(true);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [initialLoadDone]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties, refreshTrigger]);

  // Handle Stripe checkout return
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('billing_session_id');
    if (!sessionId) return;
    (async () => {
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.status === 'complete') {
          toast.success(
            data.subscription_status === 'trialing'
              ? `Your ${data.plan || ''} trial has started!`
              : 'Subscription active.'
          );
        } else {
          toast.info('Checkout did not complete. You can try again from /pricing.');
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not verify checkout.');
      } finally {
        // Clean URL
        params.delete('billing_session_id');
        navigate(
          { pathname: location.pathname, search: params.toString() },
          { replace: true }
        );
      }
    })();
  }, [location.search, location.pathname, navigate]);

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const getActiveView = () => {
    const path = location.pathname;
    if (path === '/calendar') return 'calendar';
    if (path === '/calendar-sync') return 'calendar-sync';
    if (path === '/properties') return 'properties';
    if (path === '/files') return 'files';
    if (path === '/bookings') return 'bookings';
    if (path === '/cleaning') return 'cleaning';
    if (path === '/maintenance') return 'maintenance';
    if (path === '/renovations') return 'renovations';
    if (path === '/expenses') return 'expenses';
    if (path === '/workers') return 'workers';
    if (path === '/reports') return 'reports';
    if (path === '/invoices') return 'invoices';
    if (path === '/work-orders') return 'work-orders';
    if (path === '/users') return 'users';
    if (path === '/billing') return 'billing';
    if (path === '/account') return 'account';
    if (path === '/team-chat') return 'team-chat';
    if (path === '/waitlist') return 'waitlist';
    if (path === '/email-health') return 'email-health';
    if (path === '/analytics') return 'analytics';
    return 'home';
  };

  const renderContent = () => {
    const view = getActiveView();

    switch (view) {
      case 'calendar':
        return (
          <CalendarView 
            selectedProperty={selectedProperty} 
            properties={properties}
            onRefresh={triggerRefresh}
          />
        );
      case 'calendar-sync':
        return (
          <CalendarSyncView 
            selectedProperty={selectedProperty} 
            properties={properties}
            onRefresh={triggerRefresh}
          />
        );
      case 'properties':
        return (
          <PropertiesView 
            properties={properties} 
            onRefresh={triggerRefresh}
            onSelectProperty={(prop) => {
              setSelectedProperty(prop);
              navigate('/calendar');
            }}
          />
        );
      case 'files':
        return <FilesView />;
      case 'bookings':
        return (
          <BookingsView 
            selectedProperty={selectedProperty}
            properties={properties}
            onRefresh={triggerRefresh}
          />
        );
      case 'cleaning':
        return (
          <CleaningView 
            selectedProperty={selectedProperty}
            properties={properties}
            onRefresh={triggerRefresh}
          />
        );
      case 'maintenance':
        return (
          <MaintenanceView 
            selectedProperty={selectedProperty}
            properties={properties}
            onRefresh={triggerRefresh}
          />
        );
      case 'renovations':
        return (
          <RenovationsView 
            selectedProperty={selectedProperty}
            properties={properties}
          />
        );
      case 'expenses':
        return (
          <ExpensesView 
            selectedProperty={selectedProperty}
            properties={properties}
            onRefresh={triggerRefresh}
          />
        );
      case 'workers':
        return (
          <WorkersView 
            onRefresh={triggerRefresh}
          />
        );
      case 'reports':
        return (
          <ReportsView 
            selectedProperty={selectedProperty}
            properties={properties}
          />
        );
      case 'invoices':
        return (
          <InvoicesView 
            selectedProperty={selectedProperty}
            properties={properties}
            onRefresh={triggerRefresh}
          />
        );
      case 'work-orders':
        return (
          <WorkOrdersView
            selectedProperty={selectedProperty}
            properties={properties}
          />
        );
      case 'users':
        return (
          <UsersView />
        );
      case 'billing':
        return (
          <BillingView />
        );
      case 'account':
        return <AccountView />;
      case 'team-chat':
        return <TeamChatView />;
      case 'waitlist':
        return (
          <WaitlistView />
        );
      case 'email-health':
        return <EmailHealthView />;
      case 'analytics':
        return (
          <AnalyticsView 
            selectedProperty={selectedProperty}
            properties={properties}
          />
        );
      default:
        return (
          <DashboardHome 
            selectedProperty={selectedProperty}
            properties={properties}
            onNavigate={(path) => navigate(path)}
            onRefresh={triggerRefresh}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FDFBF7]">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <UpgradePromptProvider>
      <div className="flex min-h-screen bg-[#FDFBF7]" data-testid="dashboard-container">
        <Sidebar 
          activeView={getActiveView()}
          properties={properties}
          selectedProperty={selectedProperty}
          onSelectProperty={setSelectedProperty}
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
        />
        <div className="flex-1 flex flex-col min-w-0 md:ml-64">
          <EmailVerificationBanner />
          <MobileHeader onOpenSidebar={() => setSidebarOpen(true)} />
          <main className="flex-1 p-4 md:p-8 animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <BackButton activeView={getActiveView()} />
              <div className="ml-auto relative z-50">
                <NotificationsBell />
              </div>
            </div>
            <CustomerViewBanner />
            {renderContent()}
          </main>
        </div>
        <AIChatWidget theme="light" />
        <RolePickerDialog
          open={!!user && user.is_google_user === true && user.role_selected === false}
        />
      </div>
    </UpgradePromptProvider>
  );
}
